import { z } from 'zod';
import { Email, IsoDateTime, Locale, Uuid } from './common.js';
/** Politique de mots de passe (ASVS 2.1) : longueur ≥ 12, pas de composition imposée, liste de compromis côté serveur. */
export const Password = z.string().min(12, 'Au moins 12 caractères').max(128);
export const LoginRequest = z.object({
    email: Email,
    password: z.string().min(1).max(128),
    /** Code TOTP si la MFA est activée sur le compte (peut être fourni en 2e étape). */
    mfaCode: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
export const RoleSummary = z.object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    isSystem: z.boolean(),
    propertyId: Uuid.nullable(),
});
export const CurrentUser = z.object({
    id: Uuid,
    email: Email,
    fullName: z.string(),
    locale: Locale,
    mfaEnabled: z.boolean(),
    tenant: z.object({
        id: Uuid,
        slug: z.string(),
        legalName: z.string(),
        tradeName: z.string().nullable(),
        currencyCode: z.string().length(3),
        timezone: z.string(),
        defaultLocale: Locale,
    }),
    properties: z.array(z.object({ id: Uuid, code: z.string(), name: z.string() })),
    roles: z.array(RoleSummary),
    /** Permissions effectives (union des rôles), utilisées par le frontend pour masquer l'UI ; le serveur reste l'autorité. */
    permissions: z.array(z.string()),
    session: z.object({ expiresAt: IsoDateTime, idleExpiresAt: IsoDateTime }),
});
export const LoginResponse = z.discriminatedUnion('status', [
    z.object({ status: z.literal('AUTHENTICATED'), user: CurrentUser, csrfToken: z.string() }),
    z.object({ status: z.literal('MFA_REQUIRED'), mfaToken: z.string() }),
]);
export const MfaVerifyRequest = z.object({
    mfaToken: z.string(),
    code: z.string().regex(/^\d{6}$/),
});
export const MfaSetupResponse = z.object({
    secret: z.string(),
    otpauthUrl: z.string().url(),
    qrCodeDataUrl: z.string(),
});
export const MfaEnableRequest = z.object({ code: z.string().regex(/^\d{6}$/) });
export const MfaDisableRequest = z.object({
    password: z.string().min(1),
    code: z.string().regex(/^\d{6}$/),
});
export const ForgotPasswordRequest = z.object({ email: Email });
export const ResetPasswordRequest = z.object({ token: z.string().min(20), password: Password });
export const ChangePasswordRequest = z.object({
    currentPassword: z.string().min(1),
    newPassword: Password,
});
export const AcceptInvitationRequest = z.object({
    token: z.string().min(20),
    fullName: z.string().min(2).max(120),
    password: Password,
});
export const SessionSummary = z.object({
    id: Uuid,
    createdAt: IsoDateTime,
    lastSeenAt: IsoDateTime,
    expiresAt: IsoDateTime,
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    current: z.boolean(),
});
/** Aperçu d'une invitation (GET /auth/invitations/{token}), affiché avant l'activation du compte. */
export const InvitationPreview = z.object({
    email: Email,
    fullName: z.string(),
    tenantName: z.string(),
    roleNames: z.array(z.string()),
    expiresAt: IsoDateTime,
});
//# sourceMappingURL=auth.js.map